/**
 * Market Data Hook
 *
 * Encapsulates the CQRS flow for fetching a market recommendation:
 *   1. Compute deterministic contentId from the page URL
 *   2. GET /recommendation/markets/web (fast path)
 *   3. POST /recommendation/ingestion → poll command status (cold path) —
 *      **only when `config.allowContentIngestion === true`**; otherwise this
 *      step is skipped and flow falls through to step 5.
 *   4. GET /markets/event/:id → IMarketEventDTO (iterate top-N candidates,
 *      pick first renderable — protects against a top recommendation that
 *      hydrates to an unrenderable payload while #2/#3 would have worked)
 *   5. Fallback: GET /markets/events/featured
 *
 * Returns the canonical `IMarketEventDTO` from `@repo/types`. Components derive
 * display values via the helpers in `src/utils/market-event-helpers.ts` rather than
 * depending on a widget-specific view-model.
 */

import { useEffect, useState } from "preact/hooks";
import { ApiClient, generateWebContentId, type IMarketEventDTO } from "../api";
import { apiBaseUrl, debugLog, errorLog, getConfig } from "../config";
import { WidgetState } from "../types";
import { extractPageContent } from "../utils/extract-content";
import { isMarketEventRenderable } from "../utils/market-event-helpers";

/**
 * How many top recommendations to consider when looking for the first
 * renderable market. The recommendation service returns candidates in
 * descending relevance order; this hook walks them and picks the first
 * one whose hydrated payload passes `isMarketEventRenderable`.
 *
 * Mirrors the size of the featured-markets fallback below so both paths
 * have the same resilience to an isolated unrenderable top candidate
 * (e.g. transient REST failure, market with no active children, payload
 * drift). Five is plenty of headroom — the #1 recommendation is right in
 * the vast majority of cases.
 */
const recommendationCandidateLimit = 5;
const featuredFallbackLimit = 5;

export interface UseMarketEventDataParams {
  apiKey: string;
  tenantId: string;
  pageUrl: string;
}

export interface UseMarketEventDataResult {
  state: WidgetState;
  marketEvent: IMarketEventDTO | null;
}

export function useMarketEventData({
  apiKey,
  tenantId,
  pageUrl,
}: UseMarketEventDataParams): UseMarketEventDataResult {
  const [state, setState] = useState<WidgetState>(WidgetState.Loading);
  const [marketEvent, setMarketEvent] = useState<IMarketEventDTO | null>(null);

  useEffect(() => {
    // Reset on every input change so a stale "ready" market event from a
    // previous page / tenant / key doesn't flash while the new request is in
    // flight (and isn't retained if the new request resolves to empty/error).
    setState(WidgetState.Loading);
    setMarketEvent(null);

    if (!apiBaseUrl) {
      errorLog("No API base URL configured. Widget cannot load.");
      setState(WidgetState.Error);
      return;
    }

    const client = new ApiClient(apiBaseUrl, apiKey, tenantId);
    const controller = new AbortController();
    const { signal } = controller;

    async function loadMarket(): Promise<void> {
      try {
        const contentId = generateWebContentId(pageUrl);
        debugLog(`Computed contentId: ${contentId}`);

        const existing = await client.checkRecommendations(
          contentId,
          recommendationCandidateLimit,
        );
        if (signal.aborted) return;

        let candidateIds: string[] = [];

        if (existing && existing.length > 0) {
          candidateIds = existing.map((r) => r.marketEventId);
          debugLog(
            `Fast path hit — ${candidateIds.length} candidate(s), top: ${candidateIds[0]}`,
          );
        } else if (getConfig().allowContentIngestion) {
          const extracted = extractPageContent();
          const ingestion = await client.ingestContent({
            url: pageUrl,
            title: extracted.title,
            content: extracted.mainContent,
            headings: extracted.headings,
            keywords: extracted.keywords,
          });
          if (signal.aborted) return;

          if (ingestion) {
            const matchedIds = await client.pollCommandStatus(
              ingestion.commandId,
              signal,
            );
            if (signal.aborted) return;
            if (matchedIds && matchedIds.length > 0) {
              candidateIds = matchedIds;
              debugLog(
                `Command completed — ${candidateIds.length} candidate(s), top: ${candidateIds[0]}`,
              );
            }
          }
        } else {
          debugLog(
            'Content ingestion skipped (set data-allow-content-ingestion="true" to enable). Falling through to featured-markets fallback.',
          );
        }

        if (signal.aborted) return;

        // Walk candidates in relevance order and resolve the first that
        // hydrates to a renderable payload. Sequential (not parallel) so
        // we don't fan out N market-event requests when the #1 candidate
        // is almost always correct. Stops at the first hit.
        let resolved: IMarketEventDTO | null = null;

        for (const candidateId of candidateIds) {
          if (signal.aborted) return;
          // Per-candidate try/catch: today client.getMarketEvent swallows
          // errors and returns null, but isolating the call here means a
          // future throw (e.g. abort-signal support raising AbortError)
          // can't abort the whole walk and the featured-markets fallback
          // below — one bad candidate stays one bad candidate.
          let fetched: IMarketEventDTO | null = null;
          try {
            fetched = await client.getMarketEvent(candidateId);
          } catch (err) {
            if (signal.aborted) return;
            errorLog(
              `Candidate ${candidateId} fetch threw, trying next candidate`,
              err,
            );
          }
          if (signal.aborted) return;
          if (fetched && isMarketEventRenderable(fetched)) {
            resolved = fetched;
            break;
          }
          debugLog(
            `Candidate ${candidateId} unrenderable, trying next candidate`,
          );
        }

        if (!resolved) {
          debugLog("No recommendation found, falling back to featured markets");
          // Fetch a small batch instead of just the top one — `featured[0]`
          // may be non-renderable (malformed payload, in-flight API drift),
          // and falling back to "empty" when the next item would have worked
          // is wasteful. The server-side curation should keep this list
          // short and near-100% renderable, so 5 is plenty of headroom.
          const featured = await client.getFeaturedMarkets(
            featuredFallbackLimit,
          );
          if (signal.aborted) return;
          if (featured) {
            resolved = featured.find(isMarketEventRenderable) ?? null;
          }
        }

        if (signal.aborted) return;

        if (resolved) {
          setMarketEvent(resolved);
          setState(WidgetState.Ready);
          debugLog(`Widget ready: "${resolved.title}"`);
        } else {
          debugLog("No market data available");
          setState(WidgetState.Empty);
        }
      } catch (err) {
        if (!signal.aborted) {
          errorLog("Widget load failed:", err);
          setState(WidgetState.Error);
        }
      }
    }

    loadMarket();

    return (): void => {
      controller.abort();
    };
  }, [apiKey, tenantId, pageUrl]);

  return { state, marketEvent };
}
